import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { logExtraProfileRequest } from '../lib/extraProfileRequest';
import { AlertTriangle, ChevronRight, Users } from 'lucide-react';

/**
 * PlanCapBanner — Iter-51.63 (extended).
 *
 * Two independent banners live here, in priority order:
 *   1) Over-profile watchdog banner (manual-review OR scheduled-delete)
 *      — sourced from `/billing/status` because it's about the ACCOUNT,
 *      not per-book page usage.
 *   2) Existing plan-page-cap banner — sourced from `/me/usage`.
 *
 * Rendering rule: at most ONE banner shows at a time so the header
 * doesn't stack red-on-amber strips. Watchdog wins because a Case B
 * manual review is more urgent than an over-cap upload.
 */
export default function PlanCapBanner() {
  const [usage, setUsage] = useState(null);
  const [billing, setBilling] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/me/usage').then(({ data }) => alive && setUsage(data)).catch(() => {});
    api.get('/billing/status').then(({ data }) => alive && setBilling(data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (dismissed) return null;

  // Priority 1 — over-profile watchdog banner.
  if (billing) {
    if (billing.over_profile_needs_manual_review) {
      return (
        <div data-testid="over-profile-review-banner"
          className="relative bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2.5 shadow-md">
          <div className="max-w-6xl mx-auto flex items-center gap-3 pr-8">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <Users size={16} strokeWidth={2.8}/>
            </div>
            <div className="flex-1 min-w-0 text-sm leading-snug">
              <div className="font-extrabold">Your account is under review</div>
              <div className="text-xs text-white/95 mt-0.5">
                You have more student profiles than your plan includes, and more than one has real reading history.
                Please contact us or remove extra profiles yourself — we won't auto-delete anything.
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <a href={billing.whatsapp_url_add_child || billing.whatsapp_url_continue || '#'}
                target="_blank" rel="noopener noreferrer"
                onClick={() => logExtraProfileRequest('over_profile_manual_review_banner')}
                data-testid="over-profile-review-contact"
                className="text-[11px] font-extrabold px-3 py-1.5 rounded-full bg-white text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1">
                Message us <ChevronRight size={12} strokeWidth={3}/>
              </a>
            </div>
            <button data-testid="over-profile-review-dismiss"
              aria-label="Dismiss for this session"
              onClick={() => setDismissed(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/15 inline-flex items-center justify-center text-white/80 hover:text-white text-lg leading-none">×</button>
          </div>
        </div>
      );
    }
    if (billing.over_profile_scheduled_delete_at) {
      const deleteAt = new Date(billing.over_profile_scheduled_delete_at);
      const daysLeft = Math.max(0, Math.ceil((deleteAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
      return (
        <div data-testid="over-profile-delete-banner"
          className="relative bg-gradient-to-r from-rose-600 to-rose-500 text-white px-4 py-2.5 shadow-md">
          <div className="max-w-6xl mx-auto flex items-center gap-3 pr-8">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <AlertTriangle size={16} strokeWidth={2.8}/>
            </div>
            <div className="flex-1 min-w-0 text-sm leading-snug">
              <div className="font-extrabold">
                Extra profiles will be removed in {daysLeft} day{daysLeft === 1 ? '' : 's'}
              </div>
              <div className="text-xs text-white/95 mt-0.5">
                Your plan includes 1 profile. Scheduled for {deleteAt.toLocaleDateString()}.
                Add the ₹{billing.second_profile_addon_inr || 299} sibling add-on to keep them.
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <a href={billing.whatsapp_url_add_child || '#'}
                target="_blank" rel="noopener noreferrer"
                onClick={() => logExtraProfileRequest('over_profile_scheduled_delete_banner')}
                data-testid="over-profile-delete-keep"
                className="text-[11px] font-extrabold px-3 py-1.5 rounded-full bg-white text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                Keep profiles <ChevronRight size={12} strokeWidth={3}/>
              </a>
            </div>
            <button data-testid="over-profile-delete-dismiss"
              aria-label="Dismiss for this session"
              onClick={() => setDismissed(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/15 inline-flex items-center justify-center text-white/80 hover:text-white text-lg leading-none">×</button>
          </div>
        </div>
      );
    }
  }

  // Priority 2 — original plan-page-cap banner.
  if (!usage) return null;
  const used   = Number(usage.active_pages_used || 0);
  const limit  = Number(usage.active_pages_limit || 0);
  const plan   = usage.plan?.name || 'Free trial';
  if (limit <= 0 || used <= limit) return null;

  return (
    <div
      data-testid="plan-cap-banner"
      className="relative bg-gradient-to-r from-rose-600 to-rose-500 text-white px-4 py-2.5 shadow-md"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 pr-8">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <AlertTriangle size={16} strokeWidth={2.8}/>
        </div>
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <div className="font-extrabold" data-testid="plan-cap-banner-headline">
            You're over your {plan} page limit — {used}/{limit} pages
          </div>
          <div className="text-xs text-white/90 mt-0.5">
            New uploads are blocked. Delete pages to get back under {limit} or upgrade your plan.
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <Link
            to="/books"
            data-testid="plan-cap-banner-books"
            className="text-[11px] font-extrabold px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 inline-flex items-center gap-1"
          >
            Manage pages
          </Link>
          <Link
            to="/billing"
            data-testid="plan-cap-banner-upgrade"
            className="text-[11px] font-extrabold px-3 py-1.5 rounded-full bg-white text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
          >
            Upgrade <ChevronRight size={12} strokeWidth={3}/>
          </Link>
        </div>
        <button
          data-testid="plan-cap-banner-dismiss"
          aria-label="Dismiss for this session"
          onClick={() => setDismissed(true)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/15 inline-flex items-center justify-center text-white/80 hover:text-white text-lg leading-none"
        >×</button>
      </div>
    </div>
  );
}
